import React, { useMemo } from 'react';
import { Button, ButtonGroup, ButtonOr } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { NewTimeSheetEntry } from './types';

interface Props {
  showRow: NewTimeSheetEntry;
  currentUserHasActiveEntry: boolean;
  currentUserActiveEntryId?: string;
  handleActionChange: (showRow: any, actionCode: number) => void;
  isOwnEntry: boolean;
}

const RenderActionButtons: React.FC<Props> = ({
  showRow,
  currentUserHasActiveEntry,
  currentUserActiveEntryId,
  handleActionChange,
  isOwnEntry,
}) => {
  // Check if this specific row is the current user's active entry
  const isThisRowActive = showRow.id === currentUserActiveEntryId;

  // Validation check - required fields must be filled
  const hasValidationErrors = useMemo(() => {
    return (
      showRow.start_Reel === 0 ||
      showRow.end_Reel === 0 ||
      showRow.task === '' ||
      showRow.scope_of_Work === '' ||
      showRow.status === ''
    );
  }, [
    showRow.start_Reel,
    showRow.end_Reel,
    showRow.task,
    showRow.scope_of_Work,
    showRow.status,
  ]);

  /**
   * Start button disabled when:
   * 1. Current user already has an active entry elsewhere (and this isn't that entry)
   * 2. This row belongs to another user
   * 3. Required fields are not filled
   */
  const isStartDisabled = useMemo(() => {
    // If this row belongs to another user, disable
    if (!isOwnEntry) return true;

    // If validation fails, disable
    if (hasValidationErrors) return true;

    // If current user has an active entry and this row is NOT that entry, disable
    if (currentUserHasActiveEntry && !isThisRowActive) return true;

    return false;
  }, [currentUserHasActiveEntry, isThisRowActive, isOwnEntry, hasValidationErrors]);

  /**
   * Hold/Complete disabled when:
   * 1. This row belongs to another user
   * 2. Required fields are not filled
   */
  const isHoldCompleteDisabled = useMemo(() => {
    return !isOwnEntry || hasValidationErrors;
  }, [isOwnEntry, hasValidationErrors]);

  /**
   * Resume button disabled when:
   * 1. Current user already has another active entry
   * 2. This row belongs to another user
   */
  const isResumeDisabled = useMemo(() => {
    // If this row belongs to another user, disable
    if (!isOwnEntry) return true;

    // If current user has an active entry and this row is NOT that entry, disable
    if (currentUserHasActiveEntry && !isThisRowActive) return true;

    return false;
  }, [currentUserHasActiveEntry, isThisRowActive, isOwnEntry]);

  // Render based on current action state
  switch (showRow.actions) {
    case '1': // Started
    case '4': // Resumed
      return (
        <ButtonGroup size="mini">
          <Button
            color="yellow"
            onClick={() => handleActionChange(showRow, 3)}
            disabled={isHoldCompleteDisabled}
          >
            Hold
          </Button>
          <ButtonOr />
          <Button
            color="blue"
            onClick={() => handleActionChange(showRow, 2)}
            disabled={isHoldCompleteDisabled}
          >
            Complete
          </Button>
        </ButtonGroup>
      );

    case '3': // On Hold
      return (
        <Button
          onClick={() => handleActionChange(showRow, 4)}
          disabled={isResumeDisabled}
        >
          Resume
        </Button>
      );

    case '2': // Completed
      return (
        <>
          <span style={{ color: 'green' }}>✔</span> Completed
        </>
      );

    default: // No action yet (new entry)
      return (
        <Button
          primary
          onClick={() => handleActionChange(showRow, 1)}
          disabled={isStartDisabled}
        >
          Start
        </Button>
      );
  }
};

export default observer(RenderActionButtons);
